import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatError, fileUrl } from "@/api";
import { Btn, Card, StatusBadge, Tag, Spinner, TextArea, Field } from "@/components/ui-kit";
import { EmptyState, DetailRow } from "@/components/dashboard-bits";
import { Wallet, MapPin, Users, Calendar, Globe, Handshake, FileText, ArrowLeft, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { AffiliateDetail } from "@/components/AffiliateDetail";

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(undefined);
  const [form, setForm] = useState({ message: "", expected_price: "", delivery_time: "" });
  const [loading, setLoading] = useState(false);
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    let active = true;
    api.get(`/campaigns/${id}`)
      .then((r) => { if (active) { setCampaign(r.data); setApplied(!!r.data.has_applied); } })
      .catch(() => { if (active) setCampaign(null); });
    return () => { active = false; };
  }, [id]);

  const apply = async () => {
    setLoading(true);
    try {
      await api.post("/applications", { campaign_id: campaign.campaign_id, ...form });
      toast.success("Hakemus lähetetty!");
      setApplied(true);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (campaign === undefined) return <Spinner />;

  const websiteHref = campaign && campaign.website
    ? (/^https?:\/\//i.test(campaign.website) ? campaign.website : `https://${campaign.website}`)
    : "";

  return (
    <div className="max-w-3xl mx-auto pb-16" data-testid="campaign-detail-page">
      <button onClick={() => navigate(-1)} data-testid="back-button"
        className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-[#6E675C] hover:text-[#292827] transition-colors">
        <ArrowLeft size={16} /> Takaisin
      </button>

      {campaign === null ? (
        <EmptyState title="Kampanjaa ei löytynyt" subtitle="Kampanja ei ole saatavilla." />
      ) : campaign.kind === "affiliate" ? (
        <AffiliateDetail c={campaign} applied={applied} setApplied={setApplied} />
      ) : (
        <>
          {/* Campaign info + description on a clean white card */}
          <Card className="p-8 md:p-10" data-testid="campaign-detail-card">
            <div className="text-[13px] text-[#968F85]" data-testid="campaign-detail-business">{campaign.business_name}</div>
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-display text-[#292827]" data-testid="campaign-detail-title">{campaign.title}</h1>
              <StatusBadge status={campaign.is_expired ? "expired" : campaign.status} />
            </div>

            {/* Campaign images — shown above the description */}
            {campaign.images?.length > 0 && (
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="campaign-detail-images">
                {campaign.images.map((src, i) => (
                  <div key={i} className="aspect-[4/5] rounded-2xl overflow-hidden bg-[#E9EAEC]">
                    <img src={fileUrl(src)} alt="" className="h-full w-full object-cover" />
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-2">
              {(campaign.categories?.length ? campaign.categories : [campaign.category]).filter(Boolean).map((t) => <Tag key={`c-${t}`}>{t}</Tag>)}
              {(campaign.services || []).map((t) => <Tag key={`s-${t}`}>{t}</Tag>)}
            </div>

            <p className="mt-6 max-w-2xl text-[15px] text-[#3A3733] leading-7 whitespace-pre-line" data-testid="campaign-detail-description">{campaign.description}</p>

            <div className="mt-8 pt-8 border-t border-[#E6E1D9] grid sm:grid-cols-2 gap-6">
              <DetailRow dark icon={Wallet} label="Budjetti" value={campaign.budget || (campaign.budget_from && campaign.budget_to ? `${campaign.budget_from}–${campaign.budget_to} €` : "—")} />
              <DetailRow dark icon={MapPin} label="Toteutustapa" value={campaign.location || campaign.work_location || "—"} />
              <DetailRow dark icon={Handshake} label="Yhteistyön tyyppi" value={campaign.collaboration_type || "—"} />
              <DetailRow dark icon={Users} label="Sisällöntuottajia haussa" value={campaign.creators_needed || "—"} />
              <DetailRow dark icon={Calendar} label="Hakuaika päättyy" value={campaign.application_deadline || "—"} />
              <DetailRow dark icon={Calendar} label="Sisällön toimitus" value={campaign.content_deadline || campaign.deadline || "—"} />
              <DetailRow dark icon={Globe} label="Kielet" value={(campaign.languages || []).join(", ") || "—"} />
            </div>

            {websiteHref && (
              <a href={websiteHref} target="_blank" rel="noopener noreferrer" data-testid="campaign-detail-website"
                className="mt-8 inline-flex items-center gap-1.5 text-[15px] font-medium text-[#5B7FB0] hover:text-[#292827] transition-colors">
                Vieraile yrityksen verkkosivuilla <ArrowUpRight size={16} />
              </a>
            )}
          </Card>

          {/* Application form — visually separated from campaign info */}
          <Card className="mt-8 p-8" data-testid="application-card">
            {applied ? (
              <div className="flex items-center gap-2 text-[#6E675C] text-[15px]" data-testid="already-applied"><FileText size={18} strokeWidth={1.75} /> Olet jo hakenut tähän kampanjaan.</div>
            ) : (campaign.status !== "open" || campaign.is_expired) ? (
              <div className="text-[15px] text-[#6E675C]" data-testid="applications-closed">{campaign.is_expired ? "Tämän kampanjan hakuaika on päättynyt." : "Tämä kampanja ei ota tällä hetkellä vastaan hakemuksia."}</div>
            ) : (
              <div className="space-y-5">
                <h2 className="text-xl font-semibold tracking-tightish text-[#292827]">Hae kampanjaan</h2>
                <TextArea label="Hakemusviesti" placeholder="Kerro miksi olet sopiva tähän kampanjaan..."
                  value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="application-message" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Hintapyyntö (valinnainen)" placeholder="esim. 800 €" value={form.expected_price}
                    onChange={(e) => setForm({ ...form, expected_price: e.target.value })} data-testid="application-price" />
                  <Field label="Arvioitu toimitusaika" placeholder="esim. 2 viikkoa" value={form.delivery_time}
                    onChange={(e) => setForm({ ...form, delivery_time: e.target.value })} data-testid="application-delivery" />
                </div>
                <Btn variant="dark" className="w-full !py-4 !text-[16px] !rounded-2xl" onClick={apply} disabled={loading || !form.message.trim()} data-testid="submit-application">
                  {loading ? "Lähetetään..." : "Hae kampanjaan"}
                </Btn>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
